import { type ReactNode } from 'react';

export type AffiliateTier = 'NONE' | 'BRONZE' | 'SILVER' | 'FOUNDERS_CIRCLE';

const TIER_CONFIG: Record<
  AffiliateTier,
  { emoji: string; label: string; gradient: string; ringColor: string }
> = {
  NONE: {
    emoji: '',
    label: 'No tier yet',
    gradient: 'from-paper-300 to-paper-400',
    ringColor: 'ring-paper-400',
  },
  BRONZE: {
    emoji: '🥉',
    label: 'Bronze',
    gradient: 'from-amber-400 to-orange-500',
    ringColor: 'ring-amber-400',
  },
  SILVER: {
    emoji: '🥈',
    label: 'Silver',
    gradient: 'from-slate-300 to-slate-500',
    ringColor: 'ring-slate-400',
  },
  FOUNDERS_CIRCLE: {
    emoji: '🥇',
    label: 'Founders Circle',
    gradient: 'from-magenta-500 via-violet-500 to-azure-500',
    ringColor: 'ring-violet-500',
  },
};

interface TierBadgeProps {
  tier: AffiliateTier;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  className?: string;
}

/**
 * Visual badge for an affiliate tier — emoji + colored gradient.
 * Use `size="sm"` in the header, `md` (default) in the dashboard, `lg` on the
 * refer-and-earn page.
 */
export function TierBadge({ tier, size = 'md', showLabel = true, className = '' }: TierBadgeProps) {
  const cfg = TIER_CONFIG[tier];

  if (tier === 'NONE') {
    return (
      <span className={`inline-flex items-center gap-1.5 text-xs text-ink-600 ${className}`}>
        {showLabel && <span>Not an affiliate yet</span>}
      </span>
    );
  }

  const sizeClasses = {
    sm: 'text-xs px-2 py-1 gap-1',
    md: 'text-sm px-3 py-1.5 gap-2',
    lg: 'text-base px-4 py-2 gap-2.5 font-semibold',
  }[size];

  const emojiSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-xl',
  }[size];

  return (
    <span
      className={`inline-flex items-center rounded-full bg-gradient-to-r ${cfg.gradient} text-white shadow-sm ${sizeClasses} ${className}`}
    >
      <span className={emojiSizeClasses}>{cfg.emoji}</span>
      {showLabel && <span className="tracking-wide">{cfg.label}</span>}
    </span>
  );
}

/**
 * Inline tier "card" with current rates — for use on the dashboard or in account profile.
 */
export function TierCard({
  tier,
  usdtPercent,
  aimlPercent,
  leadershipOverridePercent,
  children,
}: {
  tier: AffiliateTier;
  usdtPercent: number;
  aimlPercent: number;
  leadershipOverridePercent: number;
  children?: ReactNode;
}) {
  const cfg = TIER_CONFIG[tier];

  return (
    <div
      className={`p-5 rounded-2xl border ring-1 ${cfg.ringColor} bg-white/80 backdrop-blur-sm`}
    >
      <div className="flex items-center justify-between mb-3">
        <TierBadge tier={tier} size="md" />
        {tier !== 'NONE' && (
          <div className="text-xs text-ink-600 uppercase tracking-wider">Current tier</div>
        )}
      </div>

      {tier !== 'NONE' && (
        <div className="grid grid-cols-3 gap-3 mt-3">
          <div>
            <div className="text-xs text-ink-600 mb-1">USDT cash</div>
            <div className="font-display font-bold text-lg">{usdtPercent}%</div>
            <div className="text-[10px] text-ink-600 uppercase">on direct sales</div>
          </div>
          <div>
            <div className="text-xs text-ink-600 mb-1">AIML bonus</div>
            <div className="font-display font-bold text-lg">{aimlPercent}%</div>
            <div className="text-[10px] text-ink-600 uppercase">on direct sales</div>
          </div>
          <div>
            <div className="text-xs text-ink-600 mb-1">Leadership</div>
            <div className="font-display font-bold text-lg">
              {leadershipOverridePercent > 0 ? `${leadershipOverridePercent}%` : '—'}
            </div>
            <div className="text-[10px] text-ink-600 uppercase">
              {leadershipOverridePercent > 0 ? 'on indirect' : 'fc only'}
            </div>
          </div>
        </div>
      )}

      {children}
    </div>
  );
}
